
import { useState } from 'react';

interface CarrierInfo {
  id: string;
  name: string;
  logo: string;
  color: string;
  bgColor: string;
  trackingUrl: (trackingNumber: string) => string;
  description: string;
}

const carriers: CarrierInfo[] = [
  {
    id: 'ups',
    name: 'UPS',
    logo: 'https://public.readdy.ai/ai/img_res/8b9cfa2def31ce0de32ca271d9256a09.jpg',
    color: 'text-[#644117]',
    bgColor: 'bg-[#FFF8F0]',
    trackingUrl: (tn: string) => `https://www.ups.com/track?tracknum=${tn}`,
    description: 'United Parcel Service',
  },
  {
    id: 'fedex',
    name: 'FedEx',
    logo: 'https://readdy.ai/api/search-image?query=FedEx%20logo%20purple%20and%20orange%20text%20on%20clean%20white%20background%2C%20simple%20flat%20corporate%20logo%20design%2C%20high%20resolution%20crisp%20edges%2C%20minimal%20style&width=120&height=120&seq=carrier-fedex-logo-1&orientation=squarish',
    color: 'text-[#4D148C]',
    bgColor: 'bg-[#F5F0FA]',
    trackingUrl: (tn: string) => `https://www.fedex.com/fedextrack/?trknbr=${tn}`,
    description: 'Federal Express',
  },
  {
    id: 'dhl',
    name: 'DHL',
    logo: 'https://readdy.ai/api/search-image?query=DHL%20logo%20red%20and%20yellow%20on%20clean%20white%20background%2C%20simple%20flat%20corporate%20logo%20design%2C%20high%20resolution%20crisp%20edges%2C%20minimal%20style&width=120&height=120&seq=carrier-dhl-logo-1&orientation=squarish',
    color: 'text-[#D40511]',
    bgColor: 'bg-[#FFF5F5]',
    trackingUrl: (tn: string) => `https://www.dhl.com/en/express/tracking.html?AWB=${tn}`,
    description: 'DHL Express',
  },
];

interface CarrierTrackingProps {
  /** The carrier id assigned to the order, if any */
  carrierId?: string;
  /** The tracking number provided by the carrier */
  trackingNumber?: string;
  /** Current order status */
  orderStatus: string;
}

export default function CarrierTracking({
  carrierId,
  trackingNumber,
  orderStatus,
}: CarrierTrackingProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const isShippedOrDelivered =
    orderStatus === 'shipped' || orderStatus === 'delivered';

  const assignedCarrier = carriers.find((c) => c.id === carrierId);

  const handleCopyTracking = (tn: string, id: string) => {
    navigator.clipboard.writeText(tn).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
          <i className="ri-ship-line text-lg text-[#2E7D32]"></i>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900 tracking-wider">
            SHIPPING CARRIERS
          </h3>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {isShippedOrDelivered
              ? 'Track your package directly on the carrier website'
              : 'Tracking info will be available once your order ships'}
          </p>
        </div>
      </div>

      {/* Assigned carrier with tracking number */}
      {assignedCarrier && trackingNumber && isShippedOrDelivered ? (
        <div
          className={`${assignedCarrier.bgColor} rounded-xl p-5 border border-gray-100`}
        >
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-white shadow-sm flex items-center justify-center flex-shrink-0 overflow-hidden p-2">
              <img
                src={assignedCarrier.logo}
                alt={assignedCarrier.name}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className={`text-base font-bold ${assignedCarrier.color}`}>
                  {assignedCarrier.name}
                </p>
                <span className="text-[10px] font-semibold tracking-wider bg-[#2E7D32] text-white px-2 py-0.5 rounded-full">
                  ACTIVE
                </span>
              </div>
              <p className="text-xs text-gray-500">
                {assignedCarrier.description}
              </p>
              <div className="flex items-center gap-2 mt-2.5">
                <span className="text-xs text-gray-400">Tracking #</span>
                <code className="text-xs font-mono font-semibold text-gray-800 bg-white/80 px-2 py-1 rounded-md">
                  {trackingNumber}
                </code>
                <button
                  onClick={() =>
                    handleCopyTracking(trackingNumber, assignedCarrier.id)
                  }
                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-white/80 transition-colors cursor-pointer"
                  title="Copy tracking number"
                >
                  <i
                    className={`${
                      copiedId === assignedCarrier.id
                        ? 'ri-check-line text-[#2E7D32]'
                        : 'ri-file-copy-line text-gray-400'
                    } text-sm`}
                  ></i>
                </button>
              </div>
            </div>
            <a
              href={assignedCarrier.trackingUrl(trackingNumber)}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#2E7D32] text-white text-sm font-semibold rounded-xl hover:bg-[#1B5E20] transition-colors cursor-pointer whitespace-nowrap shadow-sm"
            >
              <i className="ri-external-link-line text-base"></i>
              Track Package
            </a>
          </div>
        </div>
      ) : (
        /* Carrier grid – shown when no specific carrier is assigned or order not yet shipped */
        <div className="grid grid-cols-3 gap-4">
          {carriers.map((carrier) => (
            <div
              key={carrier.id}
              className={`${carrier.bgColor} rounded-xl p-4 border border-gray-100 transition-all hover:shadow-md group`}
            >
              <div className="flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-xl bg-white shadow-sm flex items-center justify-center mb-3 overflow-hidden p-1.5 group-hover:shadow-md transition-shadow">
                  <img
                    src={carrier.logo}
                    alt={carrier.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className={`text-sm font-bold ${carrier.color} mb-0.5`}>
                  {carrier.name}
                </p>
                <p className="text-[11px] text-gray-400 mb-3">
                  {carrier.description}
                </p>
                {isShippedOrDelivered ? (
                  <a
                    href={carrier.trackingUrl('')}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs font-medium text-[#2E7D32] hover:underline cursor-pointer"
                  >
                    <i className="ri-external-link-line text-sm"></i>
                    Track on {carrier.name}
                  </a>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs text-gray-400">
                    <i className="ri-time-line text-sm"></i>
                    Awaiting shipment
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Quick links footer */}
      <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
        <p className="text-[11px] text-gray-400">
          <i className="ri-information-line mr-1"></i>
          Tracking updates may take 24–48h to appear after shipment
        </p>
        <div className="flex items-center gap-4">
          {carriers.map((carrier) => (
            <a
              key={carrier.id}
              href={carrier.trackingUrl('')}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className={`text-xs font-medium ${carrier.color} hover:underline cursor-pointer whitespace-nowrap`}
            >
              {carrier.name}
              <i className="ri-arrow-right-up-line ml-0.5 text-[11px]"></i>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
